import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Loader2, Upload, MailOpen, Archive, Inbox, RotateCcw, ExternalLink } from "lucide-react";
import { api, imgSrc, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_PROJECT = { title: "", description: "", tags: "", live_url: "", image_url: "", featured: false, challenge: "", results: "", gallery: "" };

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-cyan/70";

const ProjectForm = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? { title: editing.title, description: editing.description, tags: editing.tags.join(", "), live_url: editing.live_url, image_url: editing.image_url, featured: editing.featured, challenge: editing.challenge || "", results: editing.results || "", gallery: (editing.gallery || []).join(", ") }
          : EMPTY_PROJECT
      );
    }
  }, [open, editing]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      gallery: form.gallery.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", payload);
        toast.success("Project added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-sora tracking-tight text-xl">{editing ? "Edit project" : "Add project"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5 mt-2">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Title *</label>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Chai Sutta Bar" data-testid="project-input-title" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Description</label>
            <textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} placeholder="What was the project about?" data-testid="project-input-description" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Tags (comma separated)</label>
            <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className={inputCls} placeholder="Web Design, SEO" data-testid="project-input-tags" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Live URL</label>
            <input value={form.live_url} onChange={(e) => setForm((f) => ({ ...f, live_url: e.target.value }))} className={inputCls} placeholder="https://…" data-testid="project-input-live-url" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">The challenge (case study)</label>
            <textarea rows={3} value={form.challenge} onChange={(e) => setForm((f) => ({ ...f, challenge: e.target.value }))} className={`${inputCls} resize-none`} placeholder="What problem was the client facing?" data-testid="project-input-challenge" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">The results (case study)</label>
            <textarea rows={3} value={form.results} onChange={(e) => setForm((f) => ({ ...f, results: e.target.value }))} className={`${inputCls} resize-none`} placeholder="What did the work achieve?" data-testid="project-input-results" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Gallery image URLs (comma separated)</label>
            <input value={form.gallery} onChange={(e) => setForm((f) => ({ ...f, gallery: e.target.value }))} className={inputCls} placeholder="https://…, /api/files/…" data-testid="project-input-gallery" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Image</label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="project-upload-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-medium transition-colors duration-300 hover:border-brand-cyan/60 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Upload image"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="project-file-input" />
              {form.image_url && (
                <img src={imgSrc(form.image_url)} alt="Project" className="w-16 h-16 rounded-lg object-cover border border-white/15" data-testid="project-image-preview" />
              )}
            </div>
            <input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} className={`${inputCls} mt-3`} placeholder="…or paste an image URL" data-testid="project-input-image-url" />
          </div>
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 accent-[#0055FF]" data-testid="project-input-featured" />
            Featured project
          </label>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} data-testid="project-save-button" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-7 py-3 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editing ? "Save changes" : "Add project"}
            </button>
            <button type="button" onClick={onClose} data-testid="project-cancel-button" className="rounded-full border border-white/20 px-7 py-3 text-sm font-medium text-white/70 hover:text-white transition-colors duration-300">
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_STYLES = {
  new: "bg-brand-cyan/15 text-brand-cyan border-brand-cyan/30",
  read: "bg-white/10 text-white/70 border-white/20",
  archived: "bg-white/5 text-white/40 border-white/10",
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([api.get("/admin/projects"), api.get("/admin/leads")]);
      setProjects(p.data);
      setLeads(l.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const setLeadStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update lead");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const filteredLeads = leadFilter === "all" ? leads : leads.filter((l) => l.status === leadFilter);
  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen bg-midnight text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-midnight/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <p className="font-sora font-extrabold tracking-tight">
            Zilleneal<span className="text-gradient">®</span>
            <span className="ml-3 text-xs font-inter font-normal text-white/40 uppercase tracking-[0.2em]">Admin</span>
          </p>
          <div className="flex items-center gap-5">
            <span className="text-sm text-white/50 hidden sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors duration-300">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setTab("leads")}
              data-testid="admin-tab-leads"
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${tab === "leads" ? "bg-gradient-to-r from-brand-blue to-brand-purple" : "border border-white/15 text-white/60 hover:text-white"}`}
            >
              Leads {newCount > 0 && <span className="ml-1.5 rounded-full bg-brand-cyan text-midnight text-xs px-2 py-0.5 font-bold">{newCount}</span>}
            </button>
            <button
              onClick={() => setTab("projects")}
              data-testid="admin-tab-projects"
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${tab === "projects" ? "bg-gradient-to-r from-brand-blue to-brand-purple" : "border border-white/15 text-white/60 hover:text-white"}`}
            >
              Projects
            </button>
          </div>
          {tab === "projects" && (
            <button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              data-testid="admin-add-project-button"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-6 py-2.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03]"
            >
              <Plus className="w-4 h-4" /> Add project
            </button>
          )}
        </div>

        {loading ? (
          <div className="mt-24 flex justify-center" data-testid="admin-loading">
            <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-brand-cyan animate-spin" />
          </div>
        ) : tab === "projects" ? (
          <div className="mt-8 grid gap-4" data-testid="admin-projects-list">
            {projects.length === 0 && (
              <p className="text-white/40 text-sm mt-8" data-testid="admin-projects-empty">No projects yet. Add your first one.</p>
            )}
            {projects.map((p) => (
              <div key={p.id} className="rounded-2xl border border-white/10 bg-surface p-5 flex flex-wrap items-center gap-5" data-testid={`admin-project-row-${p.id}`}>
                {p.image_url ? (
                  <img src={imgSrc(p.image_url)} alt="" className="w-20 h-14 rounded-lg object-cover border border-white/10" />
                ) : (
                  <div className="w-20 h-14 rounded-lg bg-white/5 border border-white/10" />
                )}
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-3">
                    <h3 className="font-sora font-bold tracking-tight">{p.title}</h3>
                    {p.featured && <span className="text-[10px] uppercase tracking-[0.15em] rounded-full border border-brand-purple/50 text-brand-purple px-2 py-0.5">Featured</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {p.tags.map((t) => (
                      <span key={t} className="text-xs text-white/45">{t}</span>
                    ))}
                    {p.live_url && (
                      <a href={p.live_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-cyan inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> live
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditing(p); setFormOpen(true); }}
                    data-testid={`admin-edit-project-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white/70 hover:text-white hover:border-white/40 transition-colors duration-300"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => deleteProject(p)}
                    data-testid={`admin-delete-project-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-4 py-2 text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors duration-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8" data-testid="admin-leads-list">
            <div className="flex gap-2 mb-6">
              {["all", "new", "read", "archived"].map((f) => (
                <button
                  key={f}
                  onClick={() => setLeadFilter(f)}
                  data-testid={`leads-filter-${f}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-colors duration-300 ${leadFilter === f ? "bg-white text-midnight" : "border border-white/15 text-white/55 hover:text-white"}`}
                >
                  {f}
                </button>
              ))}
            </div>
            {filteredLeads.length === 0 && (
              <p className="text-white/40 text-sm mt-6" data-testid="admin-leads-empty">No leads here yet.</p>
            )}
            <div className="grid gap-4">
              {filteredLeads.map((l) => (
                <div key={l.id} className="rounded-2xl border border-white/10 bg-surface p-6" data-testid={`admin-lead-row-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-sora font-bold tracking-tight">{l.name}</h3>
                        <span className={`text-[10px] uppercase tracking-[0.15em] rounded-full border px-2.5 py-0.5 ${STATUS_STYLES[l.status]}`} data-testid={`lead-status-${l.id}`}>
                          {l.status}
                        </span>
                      </div>
                      <a href={`mailto:${l.email}`} className="text-sm text-brand-cyan hover:underline">{l.email}</a>
                      <p className="text-xs text-white/40 mt-1">
                        {l.business && <span>{l.business} · </span>}
                        {l.budget && <span>{l.budget} · </span>}
                        {new Date(l.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {l.status !== "read" && (
                        <button onClick={() => setLeadStatus(l, "read")} data-testid={`lead-mark-read-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white/70 hover:text-white hover:border-white/40 transition-colors duration-300">
                          <MailOpen className="w-3.5 h-3.5" /> Mark read
                        </button>
                      )}
                      {l.status !== "archived" ? (
                        <button onClick={() => setLeadStatus(l, "archived")} data-testid={`lead-archive-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white/50 hover:text-white hover:border-white/40 transition-colors duration-300">
                          <Archive className="w-3.5 h-3.5" /> Archive
                        </button>
                      ) : (
                        <button onClick={() => setLeadStatus(l, "new")} data-testid={`lead-reopen-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white/70 hover:text-white transition-colors duration-300">
                          <RotateCcw className="w-3.5 h-3.5" /> Reopen
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-white/65 leading-relaxed whitespace-pre-wrap border-t border-white/5 pt-4">{l.message}</p>
                </div>
              ))}
            </div>
            {leads.length === 0 && (
              <div className="mt-6 flex items-center gap-3 text-white/40 text-sm">
                <Inbox className="w-4 h-4" /> Leads from the contact form will land here.
              </div>
            )}
          </div>
        )}
      </div>

      <ProjectForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} editing={editing} />
    </div>
  );
};

export default AdminDashboard;
